import { getSelectedTariff, useTariffQuantityRepricing } from '@src/domains/tariffs';
import { useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { productPackageProductsSelector, productSelector, tariffsState } from '@src/stores/product';
import { SellingMode } from '@src/core/buyerApiClient';
import { convertIsoDateToShortDate } from '@src/common';
import { useTariffSelection } from '@src/domains/tariffs/hooks/useTariffSelection';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import usePackageIdParam from '@src/hooks/usePackageIdParam';
import {
  mapPackageProductForSingleBookingQuantityLimits,
  resolvePackageSingleBookingQuantityLimits,
} from '@src/domains/productPackage';

export function usePackageQuantityStep() {
  const widgetId = useWidgetId();
  const productId = useSelectedProductId();
  useTariffQuantityRepricing(widgetId, productId);
  const product = useAppSelector(productSelector(productId));
  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const packageId = usePackageIdParam();
  const packageProducts = useAppSelector(productPackageProductsSelector(packageId ?? ''));
  const { productSchedules, loadedProducts } = useAppSelector((state) => ({
    productSchedules: state.product.productSchedules,
    loadedProducts: state.product.products,
  }));
  const { tariffs, selectedHour, onUpdateQuantity, acceptableQuantitiesFor } = useTariffSelection();

  const defaultTariff = tariffs.find((tariff) => getSelectedTariff(tariff, selectedHour?.minutes ?? null)?.isDefault);

  const quantity = defaultTariff?.selectedQuantity ?? 0;

  const [, currentProductTariffMax] = defaultTariff ? acceptableQuantitiesFor(defaultTariff) : [1, 0];

  const packageQuantityRange = resolvePackageSingleBookingQuantityLimits({
    products: (packageProducts ?? []).map((item) =>
      mapPackageProductForSingleBookingQuantityLimits(
        item,
        loadedProducts[item.productId]?.sellingMode ?? item.product?.sellingMode,
        {
          dates: productSchedules[item.productId]?.dates,
          defaultTariffGroup: productSchedules[item.productId]?.defaultTariffGroup,
        },
      ),
    ),
    selectedDate,
    selectedHour: selectedHour ?? undefined,
    currentProductId: productId,
    currentProductTariffMax,
  });

  const maxQuantity = packageQuantityRange.max;
  // When no product can honor the package minimum (conflicting bounds or sold-out stock on any
  // item), the stepper would otherwise sit disabled at 0 forever with no explanation.
  const hasAvailableQuantity =
    packageQuantityRange.max >= packageQuantityRange.min && maxQuantity >= packageQuantityRange.min;
  const shouldShowSelectedDate = Boolean(selectedDate) && product?.sellingMode !== SellingMode.WithoutDate;

  const handleDecrement = () => {
    if (!defaultTariff || quantity <= packageQuantityRange.min) return;
    onUpdateQuantity(defaultTariff.type.id, quantity - 1);
  };

  const handleIncrement = () => {
    if (!defaultTariff) return;
    const next = quantity < packageQuantityRange.min ? packageQuantityRange.min : quantity + 1;
    onUpdateQuantity(defaultTariff.type.id, Math.min(maxQuantity, next));
  };

  return {
    shouldShowSelectedDate,
    formattedSelectedDate: selectedDate ? convertIsoDateToShortDate(selectedDate) : null,
    hasDefaultTariff: Boolean(defaultTariff),
    hasAvailableQuantity,
    quantity,
    maxQuantity,
    handleDecrement,
    handleIncrement,
    decrementDisabled: !defaultTariff || quantity <= packageQuantityRange.min,
    incrementDisabled: quantity >= maxQuantity,
  };
}
